import { Lock } from "lucide-react";
import { motion } from "framer-motion";
import { calculateAge, formatDOB } from "@/lib/profiles";
import { useLanguage } from "@/lib/i18n";

const GENDER_LABEL = { bride: "Bride", groom: "Groom" };

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 0.61, 0.36, 1] },
  },
};

const ProfileCard = ({ profile, onView, onInterested }) => {
  const { t } = useLanguage();
  const age = profile.age ?? calculateAge(profile.dob);
  const showFullDOB = profile.privacy.dob === "full_dob" && profile.dob;
  const photoBlurred = profile.privacy.photo === "blurred";

  return (
    <motion.article
      variants={cardVariants}
      className="fm-card"
      data-testid={`profile-card-${profile.id}`}
    >
      <div className="fm-card-photo">
        <img
          src={profile.photo}
          alt={`Illustration for matrimonial profile ${profile.id}`}
          loading="lazy"
          className={photoBlurred ? "is-blurred" : ""}
        />
        {photoBlurred && (
          <span className="fm-photo-overlay" data-testid={`photo-private-${profile.id}`}>
            <Lock size={13} aria-hidden="true" />
            {t("Photo private · On request")}
          </span>
        )}
      </div>

      <div className="fm-card-body">
        <div className="fm-card-id">
          <span className="fm-profile-id">{profile.id}</span>
          <span
            className={`fm-chip fm-chip-${profile.gender}`}
            data-testid={`profile-gender-${profile.id}`}
          >
            {GENDER_LABEL[profile.gender]}
          </span>
        </div>

        <div className="fm-card-details">
          <p className="fm-detail" data-testid={`profile-age-${profile.id}`}>
            {showFullDOB
              ? `${t("Date of Birth")}: ${formatDOB(profile.dob)} · ${t("Age")} ${age}`
              : `${t("Age")}: ${age}`}
          </p>
          <p className="fm-detail">
            {profile.city}, {profile.countryShort}
          </p>
          <p className="fm-detail">{profile.education}</p>
          <p className="fm-detail">{profile.profession}</p>
          <p className="fm-detail">{profile.maritalStatus}</p>
        </div>

        {profile.intro && <p className="fm-card-intro">{profile.intro}</p>}

        <div className="fm-card-actions">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            data-testid={`profile-view-${profile.id}`}
            onClick={() => onView(profile)}
          >
            {t("View Profile")}
          </button>
          <button
            type="button"
            className="btn btn-solid btn-sm"
            data-testid={`profile-interested-${profile.id}`}
            onClick={() => onInterested(profile)}
          >
            {t("Interested?")}
          </button>
        </div>
      </div>
    </motion.article>
  );
};

export default ProfileCard;
